import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { RichTextSubject } from "@/modules/froalaToolbar/shared/RichTextSubject";
import {
  ensurePillCaretGuardsInDom,
  escapeTrailingPillCaret,
  stopBeforeFollowingText,
  withPillCaretGuards,
} from "@/shared/utils/pill-caret-guards";
import { attachAtomicPills } from "@/shared/utils/pill-atomic";
import { registerContentTrigger } from "@/shared/utils/content-trigger-registry";
import {
  isWholePillSelected,
  selectWholePill,
} from "@/shared/utils/pill-selection";
import {
  VARIABLE_BLOCK_CLASS,
  refreshBlockValues,
  templateToBlocksHtml,
  templateToBlocksInDom,
} from "@/shared/utils/variable-block";
import {
  VARIABLE_PILL_ATTR,
  VARIABLE_PILL_CLASS,
  refreshPillValues,
  templateToPillsHtml,
  templateToPillsInDom,
} from "@/shared/utils/variable-pill";
import { SingleVariablePopover } from "@components/ui/single-variable-popover";
import { ResizeAxis } from "@editor-components/ResizeAxis";
import { useNewSlidesStore } from "@editor/modules/editor/stores/use-new-slides-store";
import {
  DateVariablePopover,
  isDateVariableName,
  isNowBasedDateName,
  parseDateToken,
} from "@features/dynamic-variables/date-variable";
import { commitDateTokenChange } from "@features/dynamic-variables/date-variable/commit-date-token";
import { useCalcFormulas } from "@features/dynamic-variables/calc/use-calc-formulas";
import {
  InlineVariableTypeahead,
  getCaretRect,
  getCharBeforeCaret,
  rangeBeforeCaret,
  shouldOpenTypeahead,
  useTypeaheadVariables,
} from "@features/dynamic-variables/inline-typeahead";
import {
  trackDateVariableEdit,
  trackInsertVariable,
  VARIABLES_EVENTS,
} from "@features/dynamic-variables/analytics/variables-analytics";
import { track } from "@/features/analytics";
import { openRepeatableRowsForName } from "@features/dynamic-variables/repeatable/open-repeatable-rows-dialog";
import { repeatablePillTarget } from "@features/dynamic-variables/repeatable/scope";
import {
  useScopedVersionData,
  useVariablePillDecorator,
} from "@features/dynamic-variables/repeatable/use-scoped-version-data";
import type { PickerVariable } from "@features/dynamic-variables/variable-picker/types";
import type { RichTextEvent } from "@library-elements/text";
import { StyledFroalaEditorWrapper, TextLibrary } from "@library-elements/text";
import { debounce } from "@library/shared/utils";
import { useAppDispatch, useAppSelector } from "@store";
import {
  getDesignSystemValue,
  getEntitySettings,
  getIsInRedoUndo,
  getRepeatableTree,
  isVersionInEditor,
} from "@store/story/selectors";
import "../styles/Froala.scss";
import { TextCounterContainer } from "../styles/Text.styled";
import { getDeepestElementNode } from "../utils";
import handleOverlayClick from "../utils/handle-overlay-click";
import { TextCounter } from "./TextCounter";
import {
  detectBoundary,
  exitFormattedElement,
} from "./froala/boundary-navigator";
import { buildFroalaConfig } from "./froala/config";
import { ANIMATION_DELAYS, MAPPED_PLACEHOLDERS } from "./froala/constants";
import { createUpdateChanges } from "./froala/content-manager";
import {
  createDocumentKeydownHandler,
  createEditorKeydownHandler,
} from "./froala/event-handlers/keyboard-handler";
import { createDocMouseDownHandler } from "./froala/event-handlers/mouse-handler";
import {
  createPasteAfterCleanupHandler,
  createPasteBeforeHandler,
} from "./froala/event-handlers/paste-handler";
import {
  clearPending,
  flushPendingState,
  hasPending,
} from "./froala/pending-format-state";
import { createRefresh } from "./froala/refresh";
import { restoreLastRange, updateSelection } from "./froala/selection-manager";
import { createToolbarHandler } from "./froala/toolbar-handlers";
import { handleDynamicVariable } from "./froala/toolbar-handlers/insert-handler";
import { namespaceInsertedToken } from "./froala/toolbar-handlers/namespace-inserted-token";
import type { FroalaEditorInstance } from "./froala/types";

// Re-export constants for backwards compatibility (imported by rkpi.ts etc.)
export {
  RKPI_SPEEDS,
  RKPI_SPEED_OPTIONS,
  TEXT_STYLES,
  TEXT_STYLE_TAGS,
} from "./froala/constants";

declare const FroalaEditor: any;

interface Props {
  id: string;
  animation?: null | {
    speed: string;
    baseOpacity: string;
  };
  isAdmin: boolean;
  isFocusEnabled?: boolean;
  customOnUpdate?: (html: string) => void;
  content?: string;
  saveInterval?: number;
  disableResize?: boolean;
  textLimit?: number;
  customSettings?: Record<string, any>;
  hideTextLimit?: boolean;
}

let LAST_FOCUS_ID = "nope";

export const Froala = ({
  id,
  isFocusEnabled = true,
  customOnUpdate,
  saveInterval,
  isAdmin,
  disableResize,
  textLimit,
  hideTextLimit,
  content,
  customSettings,
}: Props) => {
  const [hasOverlay, setHasOverlay] = useState(true);
  const isInRedoUndo = useAppSelector(getIsInRedoUndo);
  const dispatch = useAppDispatch();
  const elementRef = useRef<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const lastRange = useRef<Range | null>(null);
  const selectedText = useRef<string | null>(null);

  const settings = useAppSelector((state) =>
    customSettings
      ? customSettings
      : getEntitySettings<Record<string, any>>(id)(state),
  );

  const isVersion = useAppSelector(isVersionInEditor);
  // Repeatable groups nested by containment — a pill whose name starts with a
  // group key edits ROWS, so it routes to the rows pop-up instead of the
  // single-value popover (which would only ever edit row 1).
  const repeatableRoots = useAppSelector(getRepeatableTree);
  // Scoped for this element's repeatable chain: a nested group's rows come
  // from ROW 1 of each enclosing group, so `{{skills.skill}}` inside
  // `team ⊃ skills` resolves against `team[0].skills`. Identical to the raw
  // version data for every element outside a nested chain.
  const versionData = useScopedVersionData(id);
  const versionDataRef = useRef(versionData);
  versionDataRef.current = versionData;

  // `rect` is captured synchronously at the click: the live editor can
  // replace pill nodes right after (Froala normalisation, a pill refresh), and
  // a detached anchor measures as a zero rect — the popover would then land
  // at the viewport's top-left. `anchorIndex` lets the popover re-find the
  // pill among the editor's pills so it keeps tracking scroll/resize. Same
  // pattern as the date popover here and the value popover in EditableContent.
  const [variablePopover, setVariablePopover] = useState<{
    anchor: HTMLElement;
    anchorIndex: number;
    rect: DOMRect;
    name: string;
    valueType: "text" | "date";
  } | null>(null);

  // Date-variable pills open a dedicated format/calculation popover (authoring
  // AND version mode), rewriting the pill token on save.
  const [datePopover, setDatePopover] = useState<{
    anchor: HTMLElement;
    rect: DOMRect;
    token: string;
  } | null>(null);

  // Inline "{{" variable typeahead — the popover owns focus (via its own
  // search input, see InlineVariableTypeahead) from the moment it opens, so
  // Froala.tsx only needs to (a) detect the "{{" trigger to open it, capture
  // the editor caret Range/rect at that moment, and deterministically hand
  // focus to the popover; and (b) know whether it's currently open so it can
  // avoid force-closing it on the blur that handing off focus causes.
  //
  // `typeaheadOpenRef` flips true synchronously the instant the trigger is
  // detected (before React even commits a render) so the very next event —
  // even one landing before the popover's search input actually takes focus
  // — is guarded. `typeaheadAnchor` is the state that actually drives
  // mounting the popover; it's set once (fixed for the whole open session)
  // right after the just-typed "{{" has landed in the DOM.
  const typeaheadOpenRef = useRef(false);
  const [typeaheadAnchor, setTypeaheadAnchor] = useState<DOMRect | null>(null);

  const closeTypeahead = () => {
    typeaheadOpenRef.current = false;
    setTypeaheadAnchor(null);
  };

  // Escape / an empty-input Backspace: the user is cancelling the pick but
  // is still conceptually "in" the trigger, so — unlike a plain dismissal
  // (outside click, the search input blurring elsewhere) — focus and the
  // caret must return to right after the literal "{{" so typing resumes
  // immediately.
  const cancelTypeahead = () => {
    closeTypeahead();
    const editor = froalaInstance.current;
    if (!editor) return;
    const sel = window.getSelection();
    if (sel) {
      const current = restoreLastRange(lastRange, sel);
      // The literal "{{" that opened the popover goes with it. Left behind, a
      // cancelled pick was a half-typed token in the content — and the
      // manifest scan (collect-story-variables.ts) paired it with the JSON's
      // own closing `}}` into a phantom variable that the plan gate listed as
      // `</p>","name":"RichText"`.
      const trigger = current ? rangeBeforeCaret(current, 2) : null;
      if (trigger) {
        trigger.deleteContents();
        sel.removeAllRanges();
        sel.addRange(trigger);
        lastRange.current = trigger.cloneRange();
        editor.save.save();
      }
    }
    editor.$el[0]?.focus();
  };

  const { variables: typeaheadVariables, isLoading: isTypeaheadLoading } =
    useTypeaheadVariables();

  // Custom (user-created) date-typed variables aren't in the built-in date
  // registries, so isDateVariableName can't recognize them by name alone.
  // Feed their bare token names in as extraDateNames so clicking e.g.
  // `{{date_plus_days}}` opens the date popover, same as a built-in date var.
  const customDateNames = useMemo(
    () =>
      new Set(
        typeaheadVariables
          .filter((variable) => variable.type === "date")
          .map((variable) =>
            variable.token.replace(/^\{\{/, "").replace(/\}\}$/, ""),
          ),
      ),
    [typeaheadVariables],
  );

  // Per-pill decoration: calc pills show the computed value (or the
  // "calculation" label while operands are empty) with the formula tooltip,
  // date pills the resolved date (version mode), repeatable-field pills row 1
  // of the scoped data with the repeatable badge; everything else stays a
  // plain pill. See shared/utils/variable-pill.ts's PillDecorator. Declared
  // after `customDateNames`, which the date decorator needs.
  const decorate = useVariablePillDecorator(id, versionData, customDateNames);
  // Calc names for the click classifier (a calc pill has no popover).
  const calcFormulas = useCalcFormulas();
  const calcFormulasRef = useRef(calcFormulas);
  calcFormulasRef.current = calcFormulas;
  const decorateRef = useRef(decorate);
  decorateRef.current = decorate;

  const [amountOfLines, setAmountOfLines] = useState(0);
  const [charCount, setCharCount] = useState<number | null>(null);

  const froalaInstance = useRef<FroalaEditorInstance | null>(null);
  // Disposer for the atomic-pill selection/beforeinput guards attached to the
  // editable in onInitialized (see shared/utils/pill-atomic.ts).
  const detachAtomicPills = useRef<(() => void) | null>(null);

  const highlightResetTimeout = useRef<NodeJS.Timeout | null>(null);

  const hasContentChanged = useRef(false);
  const isFocusedRef = useRef(false);
  const [hasInitedFroala, setHasInitiedFroala] = useState(false);

  const hasContent = settings.content && settings.content.length > 0;

  // The content as it lives in the store (`{{token}}` template form) — what a
  // save serialises back to, and therefore what the undo/redo echo guard
  // below compares against.
  let templateContent = hasContent ? settings.content : "";
  if (settings.placeholder && !hasContent) {
    const firstFourChars = settings.placeholder?.slice(0, 4);
    templateContent =
      MAPPED_PLACEHOLDERS[firstFourChars as keyof typeof MAPPED_PLACEHOLDERS] ??
      "";
  }
  if (content) templateContent = content;

  let finalContent = templateContent ?? "";
  if (isVersion) {
    // Triple-brace blocks first (version mode only), then double-brace pills.
    finalContent = templateToBlocksHtml(finalContent, versionData);
  }
  finalContent = templateToPillsHtml(finalContent, versionData, decorate);
  finalContent = withPillCaretGuards(finalContent);

  const isHyperLinkBold = useAppSelector(getDesignSystemValue("hyperlinkBold"));
  const isHyperLinkUnderline = useAppSelector(
    getDesignSystemValue("hyperlinkUnderline"),
  );
  const isHyperLinkItalic = useAppSelector(
    getDesignSystemValue("hyperlinkItalic"),
  );

  // Toolbar handler is created once inside the init effect below and closes
  // over the design-system hyperlink settings. Mirror the live values into a
  // ref so the handler reads the latest values without re-binding (matches
  // the existing `triggerContentUpdateRef` pattern).
  const hyperLinkSettingsRef = useRef({
    isHyperLinkBold,
    isHyperLinkUnderline,
    isHyperLinkItalic,
  });
  hyperLinkSettingsRef.current = {
    isHyperLinkBold,
    isHyperLinkUnderline,
    isHyperLinkItalic,
  };

  const triggerContentUpdate = (payloadId: string, content?: string) => {
    if (payloadId !== id) return;
    try {
      const editor = froalaInstance.current;
      if (!editor) return;
      const next = content ?? finalContent;
      let transformed = next ?? "";
      if (isVersion) {
        transformed = templateToBlocksHtml(transformed, versionDataRef.current);
      }
      transformed = templateToPillsHtml(
        transformed,
        versionDataRef.current,
        decorateRef.current,
      );
      transformed = withPillCaretGuards(transformed);
      editor.html.set(transformed);
    } catch (err) {
      console.warn("[Froala] triggerContentUpdate failed:", err);
    }
  };

  // A12 fix: keep ref to latest triggerContentUpdate to avoid stale closure
  const triggerContentUpdateRef = useRef(triggerContentUpdate);
  triggerContentUpdateRef.current = triggerContentUpdate;

  /**
   * Turn `{{token}}` text a paste just inserted into pill atoms — the same
   * transform the initial-content path applies (templateToPillsHtml +
   * withPillCaretGuards above), run against the live editable instead of an
   * HTML string. Copying a pill (or typing/pasting a raw token) otherwise
   * left the literal `{{name}}` text in the editor: the version-mode effect
   * further down only re-scans when `versionData` changes, which a paste
   * never does. `refreshPillValues` then normalizes labels on pills that
   * arrived as markup (a resolved value pasted into the template shows its
   * token again), matching the per-mode effects below.
   */
  const rehydratePastedPills = (root: HTMLElement) => {
    templateToPillsInDom(root, versionDataRef.current, decorateRef.current);
    refreshPillValues(
      root,
      isVersion ? versionDataRef.current : undefined,
      decorateRef.current,
    );
    ensurePillCaretGuardsInDom(root);
  };
  const rehydratePastedPillsRef = useRef(rehydratePastedPills);
  rehydratePastedPillsRef.current = rehydratePastedPills;

  // Keyed registration, NOT a single `window.__CONTENT_TRIGGER_UPDATE__`: with
  // several editors mounted at once (a table's per-cell instances) the global
  // only ever pointed at the last one to mount, so every push aimed at another
  // id hit that instance's `payloadId !== id` guard and vanished — a variable
  // inserted into a table cell stayed raw `{{token}}` text. The legacy global
  // survives as a shim over the same registry (content-trigger-registry.ts).
  useEffect(
    () =>
      registerContentTrigger(id, (...args) =>
        triggerContentUpdateRef.current(...args),
      ),
    [id],
  );

  // The content string the undo/redo write-back just pushed into the editor.
  // Froala's debounced save then echoes that exact content back through
  // `onSaveBefore` → `updateChanges`; recording it as a history frame cleared
  // `history.future` and killed redo (intermittently — the 500 ms save
  // interval raced the 500 ms `isInRedoUndo` reset). The echo is a no-op edit,
  // so it is dispatched with `skipHistory`.
  const undoRedoEchoRef = useRef<string | null>(null);
  const isInRedoUndoRef = useRef(isInRedoUndo);
  isInRedoUndoRef.current = isInRedoUndo;

  useEffect(() => {
    if (!isInRedoUndo || !froalaInstance.current) return;
    const editor = froalaInstance.current;
    try {
      undoRedoEchoRef.current = templateContent ?? "";
      editor.html.set(finalContent);
      editor.selection.setAtEnd(editor.$el.get(0));
      editor.selection.restore();
    } catch (err) {
      console.warn("[Froala] redo/undo html.set failed:", err);
    }
  }, [isInRedoUndo, finalContent]);

  // ======== COLLAPSIBLE TEXT ========
  useEffect(() => {
    if (settings.name !== "CollapsibleText") return;
    const editor = froalaInstance.current;
    if (!editor) return;

    RichTextSubject.emit({
      type: "setTextAlign",
      data: settings.titleHorizontalAlignment,
      editorId: id,
    });
  }, [settings.titleHorizontalAlignment]);
  // ======== COLLAPSIBLE TEXT ========

  const updateAnimations = debounce(() => {
    if (!settings.animation) return;

    if (elementRef.current && froalaInstance.current) {
      const delay =
        ANIMATION_DELAYS[settings.animation?.speed ?? "medium"] ??
        ANIMATION_DELAYS.medium;

      const els = elementRef.current.querySelectorAll("p, h1, h2, h3, li");
      els.forEach((e, i) => {
        if (e instanceof HTMLElement) {
          if (settings.animation) {
            e.style.setProperty("--fr-anim-delay", `${delay * i}ms`);
          } else {
            e.style.removeProperty("--fr-anim-delay");
          }
        }
      });

      froalaInstance.current?.save?.save();
    }
  }, 100);

  useEffect(() => {
    if (!froalaInstance.current) return;

    // 1. Set delays synchronously BEFORE triggering animation
    if (settings.animation && elementRef.current) {
      const delay =
        ANIMATION_DELAYS[settings.animation?.speed ?? "medium"] ??
        ANIMATION_DELAYS.medium;
      const els = elementRef.current.querySelectorAll("p, h1, h2, h3, li");
      els.forEach((e, i) => {
        if (e instanceof HTMLElement) {
          e.style.setProperty("--fr-anim-delay", `${delay * i}ms`);
        }
      });
      froalaInstance.current?.save?.save();
    }

    // 2. Trigger animation reset (consolidated from separate effect)
    if (wrapperRef.current && settings.animation) {
      wrapperRef.current.setAttribute("data-is-animation-active", "false");
      void wrapperRef.current.offsetHeight;
      wrapperRef.current.setAttribute("data-is-animation-active", "true");
    }

    if (elementRef.current) {
      const highlights = elementRef.current.querySelectorAll(
        "[data-highlight-active]",
      );
      highlights.forEach((el) => {
        el.setAttribute("data-highlight-active", "false");
        void (el as HTMLElement).offsetHeight;
        el.setAttribute("data-highlight-active", "true");
      });
    }

    froalaInstance.current.refreshToolbar();
  }, [
    settings.animation?.speed,
    settings.animation?.baseOpacity,
    hasInitedFroala,
  ]);

  useEffect(() => {
    if (
      !froalaInstance.current ||
      !elementRef.current ||
      !settings.horizontalAlignment
    )
      return;
    const els = elementRef.current.querySelectorAll('[style*="text-align"]');
    for (const el of Array.from(els)) {
      if (el instanceof HTMLElement) {
        el.style.removeProperty("text-align");
      }
    }
    froalaInstance.current?.save?.save();
    froalaInstance.current.refreshToolbar();
  }, [settings.horizontalAlignment]);

  useEffect(() => {
    if (froalaInstance.current || !Froala) return;

    const updateChanges = createUpdateChanges(
      id,
      froalaInstance,
      hasContentChanged,
      dispatch,
      customOnUpdate,
      undoRedoEchoRef,
      isInRedoUndoRef,
    );

    const refresh = createRefresh(
      id,
      froalaInstance,
      lastRange,
      selectedText,
      setAmountOfLines,
    );

    const handleAllSelection = () => {
      const editor = froalaInstance.current;
      if (!editor || !editor.selection) return false;
      if (editor.selection.isCollapsed()) {
        editor.commands.exec("selectAll");
        return true;
      }
      return false;
    };

    const toolbarHandler = createToolbarHandler({
      id,
      lastRange,
      selectedText,
      highlightResetTimeout,
      handleAllSelection,
      refresh,
      getHyperLinkSettings: () => hyperLinkSettingsRef.current,
    });

    const handleToolbarChange = (e: RichTextEvent) => {
      const editor = froalaInstance.current;
      if (!editor) return;
      try {
        toolbarHandler(e, editor);
      } catch (err) {
        console.error("[Froala] Toolbar handler error:", err);
      }
    };

    const baseEditorKeydownHandler = createEditorKeydownHandler();
    const editorKeydownHandler = function (
      this: FroalaEditorInstance,
      e: KeyboardEvent,
    ) {
      // ---- inline "{{" variable typeahead ----
      // Additive and fully guarded: inert on every keystroke except the "{"
      // that completes a "{{" trigger (cheap `e.key === "{"` check below), or
      // while the typeahead is already open/opening (tracked via
      // `typeaheadOpenRef`, a plain ref so this closure never reads a stale
      // value). Every other key, on every other keystroke, reaches
      // `baseEditorKeydownHandler` exactly as before.
      if (typeaheadOpenRef.current) {
        // The popover's own search input owns keyboard input for the whole
        // open session — typing to filter, Up/Down/Enter/Tab/Escape (see
        // InlineVariableTypeahead) — not the editor. A stray keydown can
        // still land here in the brief window before that input actually
        // takes focus; swallow it rather than let it insert into the editor
        // and break the "editor DOM only ever holds the literal {{" delete
        // -range assumption `selectTypeaheadVariable` relies on.
        e.preventDefault();
        return;
      }

      // ---- escape a trailing variable pill on printable input ----
      // Pills are contenteditable=false atoms (see variable-pill.ts), so
      // the browser normally keeps the caret out; this remains as a cheap
      // safety net for a caret that still ends up at the pill's trailing
      // text position (programmatic restores, older engines). keydown fires
      // before the default insert, so nudging the caret to just after the
      // pill (into its ZWS guard, or a freshly-created one) makes the
      // character land in normal text.
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const sel = window.getSelection();
        if (sel) escapeTrailingPillCaret(sel);
      }
      // ---- end pill-escape ----

      // ---- keep the caret in a pill's guard slot on Arrow navigation ----
      // Same rationale as the pill-escape block above: the pill↔following-
      // text boundary is a single ambiguous caret position, and browsers can
      // step straight past an invisible ZWS guard instead of stopping in it.
      // ArrowRight from inside a pill's trailing edge and ArrowLeft from the
      // very start of the text right after a pill are handled explicitly
      // instead of trusting native caret movement. Both helpers are no-ops
      // (return false) everywhere else in the document.
      if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
        const sel = window.getSelection();
        const moved = sel
          ? e.key === "ArrowRight"
            ? escapeTrailingPillCaret(sel)
            : stopBeforeFollowingText(sel)
          : false;
        if (moved) e.preventDefault();
      }
      // ---- end pill-guard arrow handling ----

      // `!isVersion`: A version fills a template's variables in; it never defines new ones. The manifest the share dialog and the publish bake read is derived from the MASTER story, so a variable inserted here would resolve to nothing on the link.
      if (
        !isVersion &&
        e.key === "{" &&
        shouldOpenTypeahead(e.key, getCharBeforeCaret())
      ) {
        // The second "{" hasn't landed in the DOM yet (keydown fires before
        // the default insert) — don't preventDefault so it lands normally,
        // then capture the resulting caret Range/rect on the next animation
        // frame and deterministically blur the editor.
        //
        // THE KEY FIX: previously the popover's search input only ever
        // *autofocused*, racing a still-focused Froala contentEditable for
        // who actually ends up with focus — a race Froala could win often
        // enough that the search input silently never got real keyboard
        // input. Explicitly blurring here removes Froala's focus
        // deterministically the same frame the popover opens, so the
        // autofocus lands cleanly instead of fighting anything. `$el.blur()`
        // is the same call `refresh.ts` and `mouse-handler.ts` already use
        // elsewhere in this file's module to programmatically blur Froala —
        // it's a real DOM blur (via Froala's jQuery-like element wrapper),
        // so it fires Froala's own `blur` event/onBlur callback below,
        // which is guarded not to close the popover for exactly this
        // transition.
        typeaheadOpenRef.current = true;
        track(VARIABLES_EVENTS.OPEN_VARIABLE_TYPEAHEAD);
        requestAnimationFrame(() => {
          const sel = window.getSelection();
          if (sel && sel.rangeCount > 0) {
            lastRange.current = sel.getRangeAt(0).cloneRange();
          }
          setTypeaheadAnchor(getCaretRect() ?? new DOMRect(0, 0, 0, 0));
          froalaInstance.current?.$el?.blur();
        });
      }
      // ---- end inline variable typeahead ----

      // Flush pending format state on printable key input
      if (
        hasPending() &&
        e.key.length === 1 &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey
      ) {
        flushPendingState();
      }
      // Clear pending state on navigation keys (cursor moved)
      if (
        hasPending() &&
        [
          "ArrowLeft",
          "ArrowRight",
          "ArrowUp",
          "ArrowDown",
          "Home",
          "End",
        ].includes(e.key)
      ) {
        clearPending();
        refresh();
      }
      baseEditorKeydownHandler.call(this, e);
    };
    const pasteBeforeHandler = createPasteBeforeHandler((root) =>
      rehydratePastedPillsRef.current(root),
    );
    const pasteAfterCleanupHandler = createPasteAfterCleanupHandler();

    const handleDocKeydown = createDocumentKeydownHandler(
      froalaInstance,
      isFocusEnabled,
      setHasOverlay,
    );

    const handleDocMouseDown = createDocMouseDownHandler(
      id,
      froalaInstance,
      elementRef,
      () => LAST_FOCUS_ID,
    );

    const handleFocus = () => {
      document.removeEventListener("keydown", handleDocKeydown);
    };

    const handleBlur = () => {
      if (isFocusEnabled) {
        document.addEventListener("keydown", handleDocKeydown);
      }
    };

    const config = buildFroalaConfig(saveInterval ?? 500, {
      onSaveBefore: (html: string) => {
        hasContentChanged.current = true;
        updateChanges(html);
      },
      onFocus: () => {
        LAST_FOCUS_ID = id;
        isFocusedRef.current = true;
        refresh();
        handleFocus();
      },
      onBlur: () => {
        isFocusedRef.current = false;
        clearPending();
        // Opening the typeahead explicitly blurs Froala (see the "{{"
        // trigger handling above) to hand focus to the popover's search
        // input. That blur must NOT close the popover: a deferred
        // activeElement check here raced the search input's autofocus and
        // tore the popover down the instant it opened. While the typeahead
        // is open the editor blur is a no-op for it — the popover owns every
        // dismissal itself (outside click via useDismiss, Escape / empty
        // Backspace via onCancel, search-input blur-to-outside via onClose).
        handleBlur();
      },
      onButtonsRefresh: () => {
        if (!isFocusedRef.current) return false;
        refresh(false, false);
      },
      onInitialized: (self: FroalaEditorInstance) => {
        self.toolbar.hide();
        if (froalaInstance.current) {
          froalaInstance.current.refreshToolbar = refresh;
        }

        const editorEl = self.$el[0];
        if (editorEl) {
          editorEl.setAttribute("data-gramm", "false");
          editorEl.setAttribute("data-gramm_editor", "false");
          editorEl.setAttribute("data-enable-grammarly", "false");

          // Variable pills stay editable spans so Froala can style them, and
          // get their "single character" behaviour from these guards instead:
          // no caret or partial selection inside a pill, no typing into it,
          // Backspace/Delete removes it whole.
          detachAtomicPills.current?.();
          detachAtomicPills.current = attachAtomicPills(
            editorEl as HTMLElement,
          );

          const deepestNode = getDeepestElementNode(editorEl);
          self.selection.setAtEnd(deepestNode);
          self.selection.restore();
          if (useNewSlidesStore.getState().consumePendingFocus(id)) {
            // Two RAFs ensure focus runs after paint; retry briefly for slow DOM.
            let attempts = 0;
            const tryFocus = () => {
              (editorEl as HTMLElement).focus();
              if (document.activeElement === editorEl) return;
              if (++attempts >= 6) return;
              requestAnimationFrame(tryFocus);
            };
            requestAnimationFrame(() => requestAnimationFrame(tryFocus));
          }
        }

        const btnEl = self.$box.closest(".buttonWrapper");
        if (btnEl.length > 0) {
          self.opts.placeholderText = "";
          self.placeholder.hide();
        }

        setHasInitiedFroala(true);
        if (isFocusEnabled) {
          setTimeout(() => {
            if (self.events) {
              refresh();
            }
          }, 0);
        } else {
          refresh();
        }
      },
      onContentChanged: () => {
        hasContentChanged.current = true;
      },
      onKeydown: editorKeydownHandler,
      onPasteBefore: function (this: FroalaEditorInstance, e: ClipboardEvent) {
        // A real paste can splice arbitrary content around the "{{" our
        // delete-range math assumes is untouched — close defensively rather
        // than risk a bad delete.
        if (typeaheadOpenRef.current) closeTypeahead();
        return pasteBeforeHandler.call(this, e);
      },
      onPasteAfterCleanup: pasteAfterCleanupHandler,
      onCharCounterUpdate: () => {
        const editor = froalaInstance.current;
        if (!editor || !editor?.charCounter) return;
        setCharCount(editor?.charCounter?.count());
      },
    });

    // REVERTED to selector binding (2026-08-19). Binding to elementRef.current
    // instead was meant to stop a duplicated slide subtree from leaving one
    // div uninitialised, but it coincided with a flood of unhandled TypeErrors
    // from inside froala-v2.js (`$tb` / popup element null) on Safari, thrown
    // from Froala's own mousemove handler on every click that activates text.
    // The selector resolves against the live document, which is what Froala's
    // instance bookkeeping has always assumed; the ref is captured inside a
    // useMemo and can point at a node other than the mounted one.
    //
    // The keep-out guard below stays: it stops us initialising before the node
    // exists, which is safe either way. The duplicate-id symptom it originally
    // addressed is prevented at source now by resetSlideIds.
    if (!elementRef.current) return;
    froalaInstance.current = new FroalaEditor(`#froala-editor-${id}`, config);

    // Track selection changes via selectionchange (covers mouseup, keyup, paste, double-click, Ctrl+A, etc.)
    // Throttled to avoid expensive DOM queries on every keystroke/mouse movement
    let selectionChangeRAF: number | null = null;
    let prevSelectionKey = "";
    const handleSelectionChange = () => {
      if (selectionChangeRAF != null) return;
      selectionChangeRAF = requestAnimationFrame(() => {
        selectionChangeRAF = null;
        const editor = froalaInstance.current;
        if (!editor) return;
        const sel = document.getSelection();
        if (!sel || sel.rangeCount === 0) return;
        const range = sel.getRangeAt(0);
        if (editor.$el?.[0]?.contains(range.commonAncestorContainer)) {
          updateSelection(froalaInstance, lastRange, selectedText);

          // Clear pending state when cursor position changes (Phase 1)
          const selKey = `${range.startOffset}:${range.endOffset}:${range.startContainer === range.endContainer}`;
          if (
            hasPending() &&
            selKey !== prevSelectionKey &&
            prevSelectionKey !== ""
          ) {
            clearPending();
            refresh();
          }
          prevSelectionKey = selKey;
        }
      });
    };
    document.addEventListener("selectionchange", handleSelectionChange);

    const unsubscribe = RichTextSubject.on((e: RichTextEvent) => {
      if (e.editorId === id) handleToolbarChange(e);
    });

    // Phase 3: Click-at-boundary cursor repositioning
    const handleMouseUp = () => {
      requestAnimationFrame(() => {
        const sel = document.getSelection();
        if (!sel || !sel.isCollapsed || sel.rangeCount === 0) return;
        const editorEl = froalaInstance.current?.$el?.[0];
        if (!editorEl) return;
        const range = sel.getRangeAt(0);
        if (!editorEl.contains(range.commonAncestorContainer)) return;

        const boundary = detectBoundary(sel);
        if (boundary.isAtBoundary && boundary.side === "start") {
          exitFormattedElement(sel, boundary);
        }
      });
    };

    document.addEventListener("mousedown", handleDocMouseDown);
    document.addEventListener("mouseup", handleMouseUp);

    if (isFocusEnabled && froalaInstance.current) {
      const editorEl = froalaInstance.current.$el?.[0];
      if (editorEl && !editorEl.contains(document.activeElement)) {
        document.addEventListener("keydown", handleDocKeydown);
      }
    }

    return () => {
      detachAtomicPills.current?.();
      detachAtomicPills.current = null;
      if (froalaInstance.current) {
        if (
          typeof froalaInstance.current.html !== "undefined" &&
          typeof froalaInstance.current.html.get === "function"
        ) {
          updateAnimations();
          updateChanges(froalaInstance.current.html.get());
        }

        if (froalaInstance.current.destroy) {
          froalaInstance.current.destroy();
          froalaInstance.current = null;
        }

        const lineBreaker = document.querySelector(".fr-line-breaker");
        if (lineBreaker) {
          lineBreaker.remove();
        }

        RichTextSubject.emit({
          type: "onFroalaDestroy",
          editorId: id,
          data: true,
        });
      }
      if (highlightResetTimeout.current != null) {
        clearTimeout(highlightResetTimeout.current);
      }
      if (selectionChangeRAF != null) cancelAnimationFrame(selectionChangeRAF);
      document.removeEventListener("selectionchange", handleSelectionChange);
      document.removeEventListener("mousedown", handleDocMouseDown);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("keydown", handleDocKeydown);
      unsubscribe();
    };
  }, []);

  // Shared pill-detection + popover-open logic. Given the element a click
  // actually landed on, walks up to the nearest variable block/pill and opens
  // the matching popover (date pills always; non-date pills only in version
  // mode). Returns which kind of pill was matched (or "none") so callers can
  // decide how to handle the originating event — this is reused both by the
  // direct mousedown listener below AND by the `.froala-overlay` click
  // handler (see handleOverlayClick), which otherwise swallows pill clicks
  // that land on the overlay before the editor is focused into edit mode.
  type PillTarget =
    | { kind: "none" }
    | { kind: "block" }
    | { kind: "date"; pill: HTMLElement; name: string }
    /** Version mode: a sender date — edit the VALUE (calendar), never the format. */
    | { kind: "value-date"; pill: HTMLElement; name: string }
    | { kind: "repeatable"; pill: HTMLElement; name: string }
    | { kind: "value"; pill: HTMLElement; name: string }
    /**
     * A pill with nothing to open — a template-mode variable, a calculation
     * (derived, nothing for the sender to edit), a built-in now-based date.
     * It still needs whole-pill selection on click: reporting these as
     * "none" left the browser's own collapsed caret sitting inside the atom,
     * and Froala's hand-rolled Backspace then emptied the span instead of
     * removing it, leaving a hollow pill behind.
     */
    | { kind: "plain"; pill: HTMLElement; name: string };

  // Pure classification of what a pointer landed on. Side effects (open a
  // popover, select the pill) are decided by the caller, because the same
  // target means different things on a first vs. second click.
  const classifyPillTarget = (target: HTMLElement | null): PillTarget => {
    const editorEl = froalaInstance.current?.$el?.[0] as
      | HTMLElement
      | undefined;
    if (!target || !editorEl) return { kind: "none" };

    // Read-only HTML block: no inline edit in the editor.
    const block = target.closest<HTMLElement>(`.${VARIABLE_BLOCK_CLASS}`);
    if (block && editorEl.contains(block)) return { kind: "block" };

    const pill = target.closest<HTMLElement>(`.${VARIABLE_PILL_CLASS}`);
    if (!pill || !editorEl.contains(pill)) return { kind: "none" };
    const name = pill.getAttribute(VARIABLE_PILL_ATTR);
    if (!name) return { kind: "none" };

    // Date variables: authoring edits the token's format/offset (template
    // settings); version mode edits the sender's DATE VALUE instead — and the
    // built-in Time/Date group (today-based) is not editable at all.
    const bareName = parseDateToken(name).name;
    if (isDateVariableName(bareName, customDateNames)) {
      if (!isVersion) return { kind: "date", pill, name };
      if (isNowBasedDateName(bareName)) return { kind: "plain", pill, name };
      return { kind: "value-date", pill, name: bareName };
    }

    // Non-date pills only edit their value in version mode.
    if (!isVersion) return { kind: "plain", pill, name };

    // A calculation is derived from other values — the pill shows the result
    // and there is nothing for the sender to edit.
    if (calcFormulasRef.current[name]) return { kind: "plain", pill, name };

    // A repeatable group's field is one cell of a row set — editing it means
    // opening the rows pop-up, focused on the group the pill belongs to. Only
    // CLASSIFIED here (this function must stay side-effect free); the opening
    // is `openRepeatableRowsForName`'s job at the call sites below.
    if (repeatablePillTarget(name, repeatableRoots)) {
      return { kind: "repeatable", pill, name };
    }

    return { kind: "value", pill, name };
  };

  const openDatePopover = (pill: HTMLElement, token: string) =>
    setDatePopover({ anchor: pill, rect: pill.getBoundingClientRect(), token });

  // Overlay path (first click on an INACTIVE text element — see
  // handleOverlayClick): the element is being activated by this very click,
  // so a pill is SELECTED here rather than opened; the next click on the
  // selected pill (handled by the mousedown listener below) opens it.
  const resolvePillPopoverClick = (
    target: HTMLElement | null,
  ):
    | "block"
    | "date"
    | "value"
    | "value-date"
    | "repeatable"
    | "plain"
    | "none" => {
    const hit = classifyPillTarget(target);
    if (
      hit.kind === "date" ||
      hit.kind === "value" ||
      hit.kind === "repeatable" ||
      hit.kind === "value-date" ||
      hit.kind === "plain"
    ) {
      selectWholePill(hit.pill);
    }
    return hit.kind;
  };

  // ======= variable pill click handling ========
  // Every pill with a popover — date (format/calculation, any mode) and
  // value (version mode) — follows the PandaDoc two-click model (Figma
  // 90:45304): the first click selects the whole atom (it is
  // contenteditable=false, so the selection is all-or-nothing), the second
  // click on the already-selected pill opens the popover. Version mode is
  // the same interaction as the template; only the pill's label differs
  // (resolved value instead of the raw token).
  useEffect(() => {
    if (!hasInitedFroala) return;
    const editorEl = froalaInstance.current?.$el?.[0] as
      | HTMLElement
      | undefined;
    if (!editorEl) return;

    // Pill whose selection is to be asserted once the browser has finished
    // processing the first click (mouseup), so the native click handling and
    // element activation run untouched first.
    let pendingSelect: HTMLElement | null = null;

    const handlePillMouseDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      const hit = classifyPillTarget(target);

      // Read-only block: fully swallowed (no inline edit).
      if (hit.kind === "block") {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      // A pill with no popover: select the whole atom (every click — there is
      // no second-click action to preserve) and let the event through so the
      // element still activates. Without this the caret lands INSIDE the pill
      // and Backspace hollows it out instead of deleting it.
      if (hit.kind === "plain") {
        pendingSelect = hit.pill;
        return;
      }

      if (
        hit.kind === "date" ||
        hit.kind === "value" ||
        hit.kind === "value-date" ||
        hit.kind === "repeatable"
      ) {
        if (isWholePillSelected(hit.pill, window.getSelection())) {
          // Second click: open, and keep the selection exactly where it is
          // (no caret move, no native drag of the selected atom, no typing
          // into the element behind the popover).
          event.preventDefault();
          event.stopPropagation();
          if (hit.kind === "date") openDatePopover(hit.pill, hit.name);
          else if (hit.kind === "repeatable")
            openRepeatableRowsForName(hit.name, repeatableRoots);
          else
            setVariablePopover({
              anchor: hit.pill,
              anchorIndex: Array.from(
                editorEl.querySelectorAll<HTMLElement>(
                  `.${VARIABLE_PILL_CLASS}`,
                ),
              ).indexOf(hit.pill),
              rect: hit.pill.getBoundingClientRect(),
              name: hit.name,
              valueType: hit.kind === "value-date" ? "date" : "text",
            });
          return;
        }
        // First click: let it through (activates the element, native atom
        // selection), then make sure the whole pill is what ends up selected.
        pendingSelect = hit.pill;
      }
      // "none" must NOT preventDefault/stopPropagation: regular clicks bubble
      // to activate the text element and a pill click should behave the same.
    };

    const handleMouseUp = () => {
      const pill = pendingSelect;
      pendingSelect = null;
      if (pill?.isConnected) selectWholePill(pill);
    };

    editorEl.addEventListener("mousedown", handlePillMouseDown, true);
    document.addEventListener("mouseup", handleMouseUp, true);
    return () => {
      editorEl.removeEventListener("mousedown", handlePillMouseDown, true);
      document.removeEventListener("mouseup", handleMouseUp, true);
    };
    // customDateNames / repeatableRoots: re-bind so the listener's captured
    // classifyPillTarget sees custom date variables once they finish loading
    // (async) and the current repeatable groups, not the empty set / tree from
    // the first render.
  }, [isVersion, hasInitedFroala, customDateNames, repeatableRoots]);

  // Rewrite the clicked date pill's token, then persist it through Froala's
  // OWN save (`save.save()` → `save.before` → updateChanges, which serializes
  // pills back to `{{token}}`). Calling the serializer directly instead bailed
  // at its dirty-flag guard — a DOM-only edit fires no Froala event to set it
  // — so an added offset never autosaved. See commitDateTokenChange.
  const handleDateTokenSave = (nextToken: string) => {
    const editor = froalaInstance.current;
    const changed = commitDateTokenChange({
      pill: datePopover?.anchor,
      nextToken,
      isVersion,
      save: () => editor?.save?.save?.(),
    });
    if (changed) trackDateVariableEdit(nextToken);
    setDatePopover(null);
  };

  // Insert the picked variable: delete the trigger "{{" back to (and
  // including) it, then reuse the existing pill-insert primitive — same one
  // the toolbar's variable picker calls (toolbar-handlers/insert-handler.ts).
  // `lastRange` was captured at open time (see editorKeydownHandler above)
  // and restored here via `restoreLastRange`, since focus itself has since
  // moved to the popover's search input.
  //
  // The filter query the user typed lives in that search input, never in the
  // editor — every editor keydown is swallowed while the typeahead is
  // open/opening, so the editor DOM only ever contains the literal "{{" the
  // trigger inserted. The delete is therefore always exactly those 2 chars,
  // not `query.length + 2` (there is no in-editor query to account for).
  const selectTypeaheadVariable = (variable: PickerVariable) => {
    const editor = froalaInstance.current;
    if (!editor) return;
    trackInsertVariable({
      variable,
      method: "typeahead",
      targetElement: "RichText",
    });

    const sel = window.getSelection();
    if (sel) {
      const current =
        restoreLastRange(lastRange, sel) ??
        (sel.rangeCount > 0 ? sel.getRangeAt(0) : null);
      const TRIGGER_LEN = 2; // "{{"
      const deleteRange = current
        ? rangeBeforeCaret(current, TRIGGER_LEN)
        : null;
      if (deleteRange) {
        deleteRange.deleteContents();
        sel.removeAllRanges();
        sel.addRange(deleteRange);
        lastRange.current = deleteRange.cloneRange();
      }
      // else: couldn't safely compute the delete range (unexpected DOM
      // state) — fall through and still insert the pill rather than dropping
      // the pick; the literal "{{" is left behind as a fallback.
    }

    // Inside a repeatable group the token must be namespaced (and registered as
    // a field) — the `{{` typeahead inserts directly, bypassing the toolbar
    // handler switch that does this for the other insert paths.
    handleDynamicVariable(
      editor,
      namespaceInsertedToken(id, variable.token),
      lastRange,
      () => {
        froalaInstance.current?.refreshToolbar?.();
      },
    );

    // Insertion moves the DOM Selection/Range back into the editor, but
    // focus itself is still on the popover's (about to close) search input —
    // pull it back so the caret actually blinks in the editor and typing
    // continues seamlessly, matching pre-typeahead behavior.
    editor.$el[0]?.focus();
  };

  // Authoring mode: a formula edited in the Σ panel (calc-formulas changed)
  // must show its new formula in the calc pill's tooltip right away — the
  // decorator writes it into the pill's `title`. Version mode is covered by
  // the effect below.
  useEffect(() => {
    if (isVersion || !hasInitedFroala) return;
    const editorEl = froalaInstance.current?.$el?.[0] as
      | HTMLElement
      | undefined;
    if (!editorEl) return;
    // Authoring mode had no raw-token re-scan at all (only version mode ran
    // `templateToPillsInDom`), so a `{{token}}` that reached the live editable
    // without going through the pill transform — an in-cell insert, the AI
    // agent — stayed literal text. Same transform + caret guards the paste
    // rehydrate applies.
    //
    // Not while this editor has focus: a token the user is part-way through
    // typing belongs to the "{{" typeahead, and swapping the text node under
    // the caret would move the caret. A push into a FOCUSED editor already
    // arrives pillified (triggerContentUpdate above).
    if (!isFocusedRef.current) {
      templateToPillsInDom(editorEl, undefined, decorate);
      ensurePillCaretGuardsInDom(editorEl);
    }
    refreshPillValues(editorEl, undefined, decorate);
  }, [isVersion, hasInitedFroala, decorate, templateContent]);

  // Convert any unparsed {{var}}/{{{var}}} text nodes (e.g. just-pasted)
  // and refresh existing pill/block values when version data updates externally.
  useEffect(() => {
    if (!isVersion || !hasInitedFroala) return;
    const editorEl = froalaInstance.current?.$el?.[0] as
      | HTMLElement
      | undefined;
    if (!editorEl) return;
    templateToBlocksInDom(editorEl, versionData);
    templateToPillsInDom(editorEl, versionData, decorate);
    refreshBlockValues(editorEl, versionData);
    refreshPillValues(editorEl, versionData, decorate);
  }, [isVersion, hasInitedFroala, versionData, decorate]);

  // Re-find the popover's pill in the LIVE editor DOM (by index among the
  // pills) so the popover follows it even after the node was replaced.
  const resolveVariablePopoverAnchor = useCallback(() => {
    if (!variablePopover) return null;
    const editorEl = froalaInstance.current?.$el?.[0] as
      | HTMLElement
      | undefined;
    const byIndex = editorEl
      ? Array.from(
          editorEl.querySelectorAll<HTMLElement>(`.${VARIABLE_PILL_CLASS}`),
        )[variablePopover.anchorIndex]
      : undefined;
    return (
      byIndex ??
      (variablePopover.anchor.isConnected ? variablePopover.anchor : null)
    );
  }, [variablePopover]);

  // Mark the active pill (popover open) so its label badge stays visible.
  useEffect(() => {
    const pill = variablePopover?.anchor;
    if (!pill) return;
    pill.setAttribute("data-active", "true");
    return () => {
      pill.removeAttribute("data-active");
    };
  }, [variablePopover?.anchor]);

  // ======= resize ========
  const [width, setWidth] = useState<number>(settings.width || 100);
  const [elementDomWidth, setElementDomWidth] = useState<number>(0);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  useEffect(() => {
    setWidth(settings.width);
  }, [settings.width]);
  const emitWidth = (width: number): void => setWidth(width);

  useEffect(() => {
    let timeout: null | NodeJS.Timeout = setTimeout(() => {
      if (elementRef?.current)
        setContainerWidth(elementRef.current.clientWidth);
      timeout = null;
    }, 250);
    if (elementRef?.current) {
      const richText: HTMLDivElement | null = elementRef.current.closest(
        '[data-element-name="RichText"]',
      );
      if (richText) setElementDomWidth(richText.clientWidth);
    }
    return () => {
      if (timeout != null) {
        clearTimeout(timeout);
      }
    };
  }, [width]);
  // ======= resize end ========

  const froalaEditorContainer = useMemo(
    () => (
      <div
        id={`froala-editor-${id}`}
        ref={elementRef}
        className={`froala-editor content rich-text-element ${isAdmin ? " sd-admin" : ""}`}
        style={{
          width: "100%",
          minHeight: 10,
        }}
        // biome-ignore lint/security/noDangerouslySetInnerHtml: <migrate later>
        dangerouslySetInnerHTML={{ __html: finalContent }}
      />
    ),
    [],
  );

  const [isCounterVisible, setIsCounterVisible] = useState<boolean>(true);

  const handleCounterVisibility = (status: Record<string, any>) => {
    setIsCounterVisible(status.counter);
  };

  return (
    <StyledFroalaEditorWrapper
      ref={wrapperRef}
      $amountOfLines={amountOfLines}
      $horizontalAlignment={settings.horizontalAlignment}
      $animation={settings.animation}
      className="[&_button.height]:!tw-hidden"
      data-rich-text-has-animation={settings.animation != null}
    >
      {hasOverlay && settings.name === "RichText" && (
        // biome-ignore lint/a11y/useKeyWithClickEvents: <migrate later>
        <span
          className="froala-overlay tw-absolute tw-inset-0 tw-left-0 tw-top-0 tw-z-10"
          onClick={(e) =>
            handleOverlayClick(e, setHasOverlay, resolvePillPopoverClick)
          }
          data-dnd-trigger
        />
      )}
      {!disableResize && !!elementRef.current?.offsetHeight && (
        <ResizeAxis
          widthProps={{
            width: String(width || 100),
            property: "width",
            min: 10,
            max: 100,
            elementDomWidth,
            containerWidth,
            emitWidth,
          }}
          heightProps={{
            height: 0,
            property: "",
            containerHeight: 0,
            emitHeight: () => {},
          }}
          containerHeight={elementRef.current?.offsetHeight}
          callbackFunc={handleCounterVisibility}
        />
      )}
      {!hasInitedFroala && (
        <TextLibrary
          entity={{ id, settings, type: "element", children: [] }}
          editorData={{ isAdmin: !!isAdmin, content: finalContent }}
        />
      )}
      <div
        style={{
          width: disableResize ? "100%" : `${width || 100}%`,
          position: "relative",
          display: hasInitedFroala ? undefined : "none",
        }}
      >
        {froalaEditorContainer}
      </div>
      <TextCounterContainer
        className="text-counter-container"
        style={{
          width: disableResize ? "100%" : `${width || 100}%`,
        }}
        $isCounterVisible={isCounterVisible}
      >
        {!hideTextLimit && settings.name !== "CollapsibleText" && (
          <TextCounter limit={textLimit || 250} current={charCount} />
        )}
      </TextCounterContainer>
      {isVersion && (
        <SingleVariablePopover
          multiline
          anchorEl={variablePopover?.anchor ?? null}
          resolveAnchorEl={resolveVariablePopoverAnchor}
          anchorRect={variablePopover?.rect ?? null}
          variableName={variablePopover?.name ?? null}
          valueType={variablePopover?.valueType ?? "text"}
          onClose={() => setVariablePopover(null)}
        />
      )}
      {datePopover && (
        <DateVariablePopover
          anchorEl={datePopover.anchor}
          anchorRect={datePopover.rect}
          token={datePopover.token}
          onClose={() => setDatePopover(null)}
          onSave={handleDateTokenSave}
        />
      )}
      {typeaheadAnchor && (
        <InlineVariableTypeahead
          variables={typeaheadVariables}
          isLoading={isTypeaheadLoading}
          anchorRect={typeaheadAnchor}
          onSelect={(variable) => {
            selectTypeaheadVariable(variable);
            closeTypeahead();
          }}
          onClose={closeTypeahead}
          onCancel={cancelTypeahead}
        />
      )}
    </StyledFroalaEditorWrapper>
  );
};
